if (!customElements.get('vtx-product-menu')) {
  class VtxProductMenu extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.tabs = [...this.querySelectorAll('[data-menu-tab]')];
      this.panels = [...this.querySelectorAll('[data-menu-panel]')];
      this.details = this.closest('details');
      this.root = this.closest('[data-vtx-header-root]');
      this.summary = this.details?.querySelector('summary');
      this.mobile = Boolean(this.closest('.vtx-product-menu--mobile'));
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.animations = new Map();
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.animations.forEach(animation => animation.cancel());
      }, {signal});
      this.hover = matchMedia('(min-width:990px)');
      this.canHover = event => this.hover.matches && !this.mobile && event.pointerType !== 'touch';
      if (!this.tabs.length || !this.details || !this.summary) return;
      const tablist = this.querySelector('[data-menu-tabs]');
      tablist.setAttribute('role', 'tablist');
      const orientation = () => tablist.setAttribute('aria-orientation', this.mobile ? 'horizontal' : 'vertical');
      orientation();
      this.tabs.forEach(tab => {
        tab.setAttribute('role', 'tab');
        const panel = this.panels.find(item => item.dataset.menuPanel === tab.dataset.menuTab);
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        tab.addEventListener('pointerenter', event => {
          if (this.canHover(event)) this.select(tab.dataset.menuTab);
        }, {signal});
        tab.addEventListener('click', event => {
          event.preventDefault();
          this.select(tab.dataset.menuTab);
        }, {signal});
        tab.addEventListener('keydown', event => {
          this.keyboard = true;
          const index = this.tabs.indexOf(tab);
          const forward = this.mobile ? 'ArrowRight' : 'ArrowDown';
          const backward = this.mobile ? 'ArrowLeft' : 'ArrowUp';
          let next = null;
          if (event.key === forward) next = (index + 1) % this.tabs.length;
          if (event.key === backward) next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next !== null) {
            event.preventDefault();
            this.select(this.tabs[next].dataset.menuTab);
            this.tabs[next].focus();
          } else if (!this.mobile && event.key === 'ArrowRight') {
            event.preventDefault();
            this.panels.find(panel => !panel.hidden)?.querySelector('a')?.focus();
          }
        }, {signal});
      });
      this.select(this.tabs[0].dataset.menuTab);
      this.setAttribute('data-menu-ready', '');
      this.summary.setAttribute('aria-expanded', String(this.details.open));
      this.details.addEventListener('toggle', () => {
        this.summary.setAttribute('aria-expanded', String(this.details.open));
        if (this.details.open) this.reveal();
        else this.animations.forEach(animation => animation.cancel());
      }, {signal});
      this.details.addEventListener('pointerenter', event => {
        clearTimeout(this.closeTimer);
        if (!this.canHover(event)) return;
        this.open();
      }, {signal});
      this.details.addEventListener('pointerleave', event => {
        if (!this.canHover(event)) return;
        this.closeTimer = setTimeout(() => {
          if (this.keyboard && this.details.contains(document.activeElement)) return;
          this.close();
        }, 180);
      }, {signal});
      this.details.addEventListener('pointerdown', () => { this.keyboard = false; }, {signal});
      this.details.addEventListener('keydown', event => {
        this.keyboard = true;
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.details.removeAttribute('data-editor-pinned');
          this.close();
          this.summary.focus();
        }
        if (event.target === this.summary && event.key === 'ArrowDown') {
          event.preventDefault();
          this.open();
          this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true')?.focus();
        }
      }, {signal});
      this.details.addEventListener('focusout', () => {
        cancelAnimationFrame(this.focusFrame);
        this.focusFrame = requestAnimationFrame(() => {
          if (!this.details.contains(document.activeElement) && !this.details.matches(':hover')) this.close();
        });
      }, {signal});
      document.addEventListener('click', event => {
        if (!this.details.contains(event.target)) this.close();
      }, {signal});
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', event => {
          if (this.mobile !== matchMedia('(max-width:989px)').matches) return;
          const card = [...this.querySelectorAll('[data-product-card]')].find(item => item.dataset.productCard === event.detail.blockId);
          const menuSelected = event.target === this.root?.querySelector('[data-vtx-products-menu]');
          if (!card && !menuSelected) return;
          if (this.mobile) {
            this.root.querySelector('[data-vtx-mobile-menu]').hidden = false;
            this.root.querySelector('[data-vtx-mobile-menu-toggle]').setAttribute('aria-expanded', 'true');
          }
          if (card) this.select(card.closest('[data-menu-panel]').dataset.menuPanel);
          this.details.setAttribute('data-editor-pinned', '');
          this.open();
          if (card) this.editorFrame = requestAnimationFrame(() => card.scrollIntoView({block:'nearest', inline:'nearest'}));
        }, {signal});
        document.addEventListener('shopify:block:deselect', () => {
          this.details.removeAttribute('data-editor-pinned');
          this.close();
        }, {signal});
      }
    }
    select(key) {
      if (!this.tabs.some(tab => tab.dataset.menuTab === key)) return;
      const changed = this.activeKey !== key;
      this.activeKey = key;
      this.tabs.forEach(tab => {
        const selected = tab.dataset.menuTab === key;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach(panel => { panel.hidden = panel.dataset.menuPanel !== key; });
      if (changed && this.details.open) {
        const panel = this.panels.find(item => !item.hidden);
        this.play(panel, [{opacity:.35,transform:'translateY(8px)'},{opacity:1,transform:'translateY(0)'}], {duration:280});
      }
    }
    play(element, frames, options = {}) {
      if (!element?.animate || this.motion.matches) return;
      this.animations.get(element)?.cancel();
      const animation = element.animate(frames, {duration:360,easing:'cubic-bezier(.16,1,.3,1)',...options});
      this.animations.set(element, animation);
      animation.finished.catch(() => {}).finally(() => {
        if (this.animations.get(element) === animation) this.animations.delete(element);
      });
    }
    reveal() {
      if (this.mobile) return;
      const flyout = this.closest('.vtx-product-menu');
      this.play(flyout, [{opacity:0,transform:'translateY(-8px) scale(.99)',clipPath:'inset(0 0 14% 0)'},{opacity:1,transform:'translateY(0) scale(1)',clipPath:'inset(0 0 0 0)'}], {duration:380});
      const panel = this.panels.find(item => !item.hidden);
      panel?.querySelectorAll('[data-product-card]').forEach((card,index) => {
        this.play(card, [{opacity:.25,transform:'translateY(12px)'},{opacity:1,transform:'translateY(0)'}], {duration:340,delay:Math.min(index,4)*28});
      });
    }
    open() {
      clearTimeout(this.closeTimer);
      this.root?.querySelectorAll('.vtx-nav-disclosure').forEach(detail => {
        if (detail !== this.details) detail.open = false;
      });
      this.details.open = true;
      this.summary.setAttribute('aria-expanded', 'true');
    }
    close() {
      clearTimeout(this.closeTimer);
      if (this.details.hasAttribute('data-editor-pinned')) return;
      this.details.open = false;
      this.summary.setAttribute('aria-expanded', 'false');
    }
    disconnectedCallback() {
      this.animations?.forEach(animation => animation.cancel());
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.focusFrame);
      cancelAnimationFrame(this.editorFrame);
    }
  }
  customElements.define('vtx-product-menu', VtxProductMenu);
}

